import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Input, Switch, Button, Row, Col, Typography, Space } from "antd";
import { toast } from "../../components/toasts/toastStore";
import SuccessOverlay from "../../components/feedback/SuccessOverlay";
import { apiAseguradoras, authHeader } from "../../redux/actions/catalogos/catalogosApi";
import PacienteFotoPicker from "./PacienteFotoPicker";
import { fotoUrl } from "./EntidadFotoCard";

import "./EventosPage.css";
import "./PaquetesPage.css";

const { Title, Text } = Typography;

export default function CrearAseguradoraPage() {
  const navigate = useNavigate();
  const { id_aseguradora } = useParams();
  const isEditing = !!id_aseguradora;

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadingItem, setLoadingItem] = useState(isEditing);
  const [success, setSuccess] = useState({ show: false, title: "", subtitle: "" });
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoActual, setFotoActual] = useState(null);

  useEffect(() => {
    if (!isEditing) return;
    setLoadingItem(true);
    apiAseguradoras
      .get(`/aseguradoras/${id_aseguradora}`, { headers: authHeader() })
      .then((res) => {
        const item = res.data ?? {};
        form.setFieldsValue({
          nombre: item.nombre,
          convenio_directo: Boolean(item.convenio_directo),
        });
        setFotoActual(fotoUrl(item));
      })
      .catch(() => toast.error("No se pudo cargar la aseguradora"))
      .finally(() => setLoadingItem(false));
  }, [id_aseguradora]);

  const subirFoto = async (id) => {
    if (!fotoFile) return;
    const fd = new FormData();
    fd.append("file", fotoFile);
    try {
      await apiAseguradoras.post(`/aseguradoras/${id}/foto`, fd, { headers: authHeader() });
    } catch (err) {
      toast.warning(err?.response?.data?.detail || "La aseguradora se guardó, pero no se pudo subir la imagen");
    }
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload = {
      nombre: values.nombre?.trim(),
      convenio_directo: Boolean(values.convenio_directo),
    };

    setSaving(true);
    try {
      if (isEditing) {
        await apiAseguradoras.put(`/aseguradoras/${id_aseguradora}`, payload, { headers: authHeader() });
        await subirFoto(id_aseguradora);
        setSuccess({ show: true, title: "Aseguradora actualizada", subtitle: "Los cambios se guardaron correctamente." });
      } else {
        const { data } = await apiAseguradoras.post("/aseguradoras", payload, { headers: authHeader() });
        await subirFoto(data?.id);
        setSuccess({ show: true, title: "Aseguradora creada", subtitle: "Se registró correctamente en el sistema." });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Error al guardar");
      setSaving(false);
    }
  };

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="cc-page-header-card">
          <Space direction="vertical" size={2}>
            <Title level={2} className="eventos-title" style={{ marginBottom: 0 }}>
              {isEditing ? "Editando aseguradora" : "Nueva aseguradora"}
            </Title>
            <Text className="eventos-subtitle">
              {isEditing
                ? "Modifica los datos y guarda los cambios."
                : "Completa los datos para registrarla en el sistema."}
            </Text>
          </Space>

          <Space>
            <Button className="eventos-btn-clean" onClick={() => navigate("/app/aseguradoras")} disabled={saving || success.show}>
              Cancelar
            </Button>
            <Button
              type="primary"
              loading={saving}
              disabled={success.show || loadingItem}
              icon={!isEditing ? <i className="fa-solid fa-plus" /> : null}
              onClick={handleSave}
              style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
            >
              {isEditing ? "Guardar cambios" : "Crear aseguradora"}
            </Button>
          </Space>
        </section>

        <Form form={form} layout="vertical" initialValues={{ convenio_directo: false }}>
          <div className="cc-section-card">
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-building-shield"></i></span>
              Datos de la aseguradora
            </div>

            <Row gutter={16}>
              <Col xs={24}>
                <div className="paq-field-label">Imagen de la aseguradora (opcional)</div>
                <PacienteFotoPicker value={fotoActual} onChange={setFotoFile} icon="fa-building-shield" />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="nombre"
                  label={<span className="paq-field-label">Nombre</span>}
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input placeholder="Nombre de la aseguradora" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="convenio_directo"
                  label={<span className="paq-field-label">Convenio directo</span>}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>

      <SuccessOverlay
        show={success.show}
        title={success.title}
        subtitle={success.subtitle}
        onDone={() => navigate("/app/aseguradoras")}
      />
    </main>
  );
}
